import { z } from 'zod';
import { ClassStatusSchema, PerformanceBandSchema, ShiftSchema } from './enums';
import { IdSchema } from './validation';

export const CreateClassSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Informe o nome da turma')
    .max(60)
    .transform((v) => v.replace(/\s+/g, ' ')),
  tutorTeacherId: IdSchema.optional(),
  shift: ShiftSchema,
  academicYearId: IdSchema,
  subjectIds: z.array(IdSchema).max(30).default([]),
  /** Alunos sem turma a vincular na criação (operação atômica). */
  studentIds: z.array(IdSchema).max(80).default([]),
});
export type CreateClassInput = z.input<typeof CreateClassSchema>;
export type CreateClassData = z.output<typeof CreateClassSchema>;

export const UpdateClassSchema = z.object({
  name: z.string().trim().min(2).max(60).optional(),
  tutorTeacherId: IdSchema.nullable().optional(),
  shift: ShiftSchema.optional(),
  subjectIds: z.array(IdSchema).max(30).optional(),
  status: ClassStatusSchema.optional(),
});
export type UpdateClassInput = z.input<typeof UpdateClassSchema>;

export const AddStudentsToClassSchema = z.object({
  studentIds: z.array(IdSchema).min(1, 'Selecione ao menos um aluno').max(80),
});
export type AddStudentsToClassInput = z.infer<typeof AddStudentsToClassSchema>;

export const ListClassesQuerySchema = z.object({
  search: z.string().trim().max(80).optional(),
  academicYearId: IdSchema.optional(),
  grade: z.string().trim().max(40).optional(),
  status: ClassStatusSchema.optional(),
  cursor: z.string().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});
export type ListClassesQuery = z.input<typeof ListClassesQuerySchema>;

export const ClassListItemSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  grade: z.string(),
  shift: ShiftSchema,
  shiftLabel: z.string(),
  academicYearId: z.string(),
  tutor: z.object({ id: z.string(), name: z.string() }).nullable(),
  studentCount: z.number().int(),
  averageScore: z.number().nullable(),
  /** Média como % da nota máxima (0–100). */
  performanceGoalPct: z.number().nullable(),
  status: ClassStatusSchema,
});
export type ClassListItem = z.infer<typeof ClassListItemSchema>;

export const ClassStudentRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  enrollmentNumber: z.string(),
  guardianName: z.string(),
  averageScore: z.number().nullable(),
  performance: PerformanceBandSchema,
  hasAvatar: z.boolean(),
});
export type ClassStudentRow = z.infer<typeof ClassStudentRowSchema>;

export const ClassDetailSchema = ClassListItemSchema.extend({
  subjects: z.array(z.object({ id: z.string(), code: z.string(), name: z.string() })),
  students: z.array(ClassStudentRowSchema),
});
export type ClassDetail = z.infer<typeof ClassDetailSchema>;

/** Extrai a série (ex.: "9º Ano") do nome da turma (ex.: "9º Ano A"). */
export function deriveGradeFromClassName(name: string): string {
  const normalized = name.trim().replace(/\s+/g, ' ');
  const match = normalized.match(/^(\d{1,2})\s*[ºª°oa]?\s*(ano|série|serie)/i);
  if (match) {
    const isSerie = match[2]!.toLowerCase().startsWith('s');
    return isSerie ? `${match[1]}ª Série` : `${match[1]}º Ano`;
  }
  const parts = normalized.split(' ');
  return parts.length > 1 ? parts.slice(0, -1).join(' ') : normalized;
}

/** Código curto da turma: "9º Ano A" → "9A". */
export function deriveClassShortCode(name: string): string {
  const match = name.match(/^(\d{1,2}).*?([A-Za-z])\s*$/);
  if (match) return `${match[1]}${match[2]!.toUpperCase()}`;
  return name
    .split(' ')
    .map((p) => p[0] ?? '')
    .join('')
    .slice(0, 3)
    .toUpperCase();
}
